import { FC } from 'react';
import { Button } from 'primereact/button';
import type { LearnosityMathRecovery } from '../../custom-hooks/useLearnosityItemsInitialization';

interface Props {
  recovery: LearnosityMathRecovery;
  onSaveAndReload: () => void;
}

/**
 * What the student reads while math is not displaying correctly. Do not promise saved answers:
 * 'saving' awaits confirmation, 'action_required' may precede a save, and 'save_failed' leaves
 * persistence unconfirmed.
 */
function recoveryMessage(recovery: Exclude<LearnosityMathRecovery, 'idle'>): string {
  switch (recovery) {
    case 'saving':
      return 'Some math did not load. Please wait while we get ready to reload this page.';
    case 'action_required':
      return 'Some math did not load. Reload the page to try again. If this keeps happening, ask your teacher for help.';
    case 'save_failed':
      return 'Your answers may not be saved yet. Keep this page open and try again.';
  }
}

/** Rendered outside Learnosity's container so the vendor never owns or replaces it. */
export const LearnosityMathRecoveryNotice: FC<Props> = ({ recovery, onSaveAndReload }) => {
  if (recovery === 'idle') return null;
  const saving = recovery === 'saving';
  return (
    <div
      key={recovery}
      role={saving ? 'status' : 'alert'}
      className="tw-w-[97%] tw-mx-auto tw-mb-4 tw-p-4 tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-amber-400 tw-bg-amber-50"
    >
      <p className="tw-m-0">{recoveryMessage(recovery)}</p>
      {!saving && (
        <Button label={recovery === 'save_failed' ? 'Try again' : 'Save and reload'} onClick={onSaveAndReload} />
      )}
    </div>
  );
};
